import * as Dialog from '@radix-ui/react-dialog';
import React, { useState } from 'react';

import PartsItem from '@/components/sections/service/parts/parts-item';
import { ButtonIcon } from '@/components/shared/buttons/button-icon';
import { Icon } from '@/components/shared/element/icons';
import { keyframes, styled } from '@/stitches.config';
import { IParts } from '@/types/shared/parts';

const overlayShow = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
});

const contentShow = keyframes({
  '0%': { opacity: 0, transform: 'translate(-50%, -48%) scale(.96)' },
  '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
});

const DialogOverlay = styled(Dialog.Overlay, {
  position: 'fixed',
  width: 'calc(100% + 1px)',
  height: 'calc(var(--viewport-height) + 1px)',
  top: -1,
  left: -1,
  bc: '$backdrop70',
  zIndex: '1001',
  cursor: 'pointer',
  '@media (prefers-reduced-motion: no-preference)': {
    animationName: `${overlayShow}`,
    animationDuration: '1s',
    animationTimingFunction: 'var(--transition-easing)',
  },
});

const DialogContent = styled(Dialog.Content, {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'calc(100% - 32px)',
  maxWidth: 520,
  zIndex: '1002',
  br: '$rounded-1-5',
  bc: '$white',
  // border: '1px solid $gray300',
  bs: '$shadow200',
  '@media (prefers-reduced-motion: no-preference)': {
    animationName: `${contentShow}`,
    animationDuration: 'var(--transition-duration)',
    animationTimingFunction: 'var(--transition-easing)',
  },
  '&:focus': { outline: 'none' },
  '@sm': {
    width: 'calc(100% - 96px)',
  },
});

const ImageTrigger = styled('button', {
  position: 'relative',
  all: 'unset',
  cursor: 'pointer',
  br: '100%',
  display: 'inline-block',
});

const PartsWrap = styled('div', {
  variants: {
    scrollArea: {
      true: {
        maxHeight: 600,
        height: 'calc(var(--viewport-height) - 48px)',
        overflow: 'hidden',
        br: '$rounded-1-5',
      },
    },
  },
});

interface IProps {
  parts: IParts;
  scrollArea?: boolean;
}

const PartsDialog: React.FunctionComponent<IProps> = (props: IProps) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  return (
    <>
      <Dialog.Root
        open={openDialog}
        onOpenChange={(open) => setOpenDialog(open)}
      >
        {!!props.parts.keySystemLayout && (
          <Dialog.Trigger asChild>
            <ImageTrigger aria-label="button">
              <Icon icon="info" size="xs" color="icon-blue" />
            </ImageTrigger>
          </Dialog.Trigger>
        )}
        <Dialog.Portal>
          <DialogOverlay />
          {!!props.parts.keySystemLayout && (
            <DialogContent css={{ maxWidth: 650 }}>
              <PartsWrap>
                <PartsItem parts={props.parts} size="small" />
                <ButtonIcon
                  onClick={() => setOpenDialog(false)}
                  icon="close"
                  size="md"
                  variant="button-icon-color-gray"
                  isTransparent
                  css={{
                    position: 'absolute',
                    right: '$space-1',
                    top: '$space-1',
                  }}
                />
              </PartsWrap>
            </DialogContent>
          )}
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};

PartsDialog.displayName = 'SectionPartsDialog';
export default PartsDialog;
